import type { ReactNode } from "react";
import { cn } from "@bindery/ui/lib/utils";

/**
 * How settled a thing is. Three tones, and deliberately no failure tone:
 *
 * `held` covers expired, abandoned and timed-out alike, and it is ochre — a
 * warm, unfinished colour — rather than red. None of those three is a failure
 * in the shopper's terms. An expired or abandoned session took no money at
 * all, and a timeout means the receipt for a payment that very likely
 * succeeded is still being issued. Painting any of them red would manufacture
 * panic about a charge that is fine, on the one surface where a person is
 * least able to check.
 */
export type StatusTone = "waiting" | "settled" | "held";

const RAIL: Record<StatusTone, string> = {
  waiting: "bg-muted-foreground/45",
  settled: "bg-verdigris",
  held: "bg-ochre",
};

const ICON: Record<StatusTone, string> = {
  waiting: "text-muted-foreground",
  settled: "text-verdigris",
  held: "text-ochre",
};

/**
 * One status, set as a ledger entry: a state icon capping a vertical rail,
 * with everything the page has to say about that state running alongside it.
 *
 * The rail is the only motion on the page while a payment is confirming, and
 * it replaces the spinner the monorepo used. A spinner is what an interface
 * shows when it has nothing specific to say; here we do — the ledger rows
 * below state exactly which of the two waits this is (authorisation, or
 * receipt issuance), so the rail only has to say "alive, still running". It
 * breathes rather than spins because the page cannot honestly claim progress
 * toward a moment it does not control.
 */
export function StatusBlock({
  tone,
  icon,
  title,
  body,
  children,
}: {
  readonly tone: StatusTone;
  readonly icon: ReactNode;
  readonly title: string;
  readonly body: ReactNode;
  /** Ledger rows, actions — anything that hangs off this status. */
  readonly children?: ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center gap-2.5 pt-px">
        <span className={cn("shrink-0", ICON[tone])}>{icon}</span>
        <span
          aria-hidden
          className={cn(
            "w-0.5 flex-1 rounded-full",
            RAIL[tone],
            tone === "waiting" && "animate-rail-breathe",
          )}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-6 pb-1">
        {/* `role="status"` + `aria-live` on the wrapper, matching the source:
            a screen-reader user is told when the phase changes without the
            focus being stolen mid-transaction. */}
        {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- holds an <h1> and paragraphs; <output> takes phrasing content only */}
        <div role="status" aria-live="polite" className="flex flex-col gap-2.5">
          <h1 className="font-serif text-[1.35rem] leading-tight tracking-[-0.01em]">
            {title}
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {body}
          </p>
        </div>
        {children}
      </div>
    </div>
  );
}
